// src/utils/authRefresh.js
import axios from "axios";
import { baseUrl } from "../context/MyContext";

// ══════════════════════════════════════════════════════
// GLOBAL AXIOS INTERCEPTOR
// Har outgoing request pe localStorage ka LATEST authToken
// lagata hai — chahe calling code ne khud koi (purana/stale)
// Authorization header pehle se set kiya ho, ye usay overwrite
// kar deta hai. Isi se refresh ke baad "purane token se request
// jana aur 401 milna" wala masla khatam hota hai.
// ══════════════════════════════════════════════════════
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// FIXED: pehle baseUrl yahan alag se hardcode kiya hua tha, aur comment
// mein likha tha "MyContext.js mein change karo to yahan bhi karna hoga" —
// yani do jagah manually sync rakhni parti thi. Ab baseUrl seedha
// MyContext.js se import ho raha hai (jahan wo ab top-level export hai) —
// sirf ek hi jagah maintain karni hai.
const refreshEndpoints = {
  Kmcustomer: `${baseUrl}/customer/auth/customerRefreshToken`,
  Kmseller: `${baseUrl}/seller/auth/sellerRefreshToken`,
  Kmadmin: `${baseUrl}/admin/auth/adminRefreshToken`,
  Kmdeliveryman: `${baseUrl}/delivery/auth/deliveryRefreshToken`,
};

/**
 * JWT token ke payload (2nd part) ko decode karta hai, bina signature verify kiye.
 * Sirf claims padhne ke liye — security check backend hi karta hai.
 */
const decodeJWT = (token) => {
  try {
    const payload = token.split(".")[1];
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = atob(normalized);
    return JSON.parse(decoded);
  } catch (error) {
    return null;
  }
};

// Har role ka apna login route — 401 (session expired) par isi pe bhejenge
const loginRoutes = {
  Kmcustomer: "/customer/login",
  Kmseller: "/seller/login",
  Kmadmin: "/admin/login",
  Kmdeliveryman: "/delivery/login",
};

let refreshIntervalId = null;

/**
 * Token khud decode kar ke uska "guard" claim padhta hai (jo backend
 * getJWTCustomClaims() mein embed karta hai — e.g. "Kmcustomer"),
 * phir sirf usi role ke sahi endpoint ko hit karta hai.
 */
export const refreshAuthToken = async () => {
  const currentToken = localStorage.getItem("authToken");
  if (!currentToken) return null;

  const decoded = decodeJWT(currentToken);
  const guard = decoded?.guard;
  const exp = decoded?.exp; // seconds since epoch — JWT ka apna standard claim

  if (!guard || !refreshEndpoints[guard]) {
    console.warn("Token mein guard claim nahi mila ya unknown guard hai:", guard);
    return null;
  }

  // Sirf tab refresh karein jab expiry qareeb ho (5 minute se kam bacha ho).
  // Warna page reload/normal use pe bhi baar baar rotate hota rehta, jo
  // dusre concurrent API calls (jo purane token se ja rahe hain) ke sath
  // race condition bana kar unhe 401 de deta.
  if (exp) {
    const remainingMs = exp * 1000 - Date.now();
    const REFRESH_THRESHOLD_MS = 5 * 60 * 1000; // 5 minute
    if (remainingMs > REFRESH_THRESHOLD_MS) {
      return null; // Abhi refresh ki zaroorat nahi
    }
  }

  try {
    const response = await axios.post(
      refreshEndpoints[guard],
      {},
      {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      },
    );

    const newToken = response?.data?.token || response?.data?.access_token;

    if (newToken) {
      localStorage.setItem("authToken", newToken);
      return newToken;
    }

    return null;
  } catch (error) {
    console.error("Token refresh failed:", error);
    // Sirf 401 (session expired/invalid token) par hi logout karein.
    // Baqi errors (network issue, backend bug, wrong route, server down)
    // par forcefully /login pe bhejna theek nahi — warna koi bhi
    // temporary backend masla poore app ko logout kar dega.
    if (error?.response?.status === 401) {
      localStorage.removeItem("authToken");
      // Guard ke hisab se sahi role ka login page pe bhejein
      const loginPath = loginRoutes[guard] || "/login";
      window.location.href = loginPath;
    }
    return null;
  }
};

/**
 * index.js mein app start hote hi call karein.
 * Interval hamesha set hota hai (chahe abhi login na hua ho) — kyunke
 * user baad mein login karega aur ye interval waisay hi chalta rahega.
 * refreshAuthToken() khud check kar leta hai ke token hai ya nahi.
 */
export const initTokenRefresh = () => {
  // Turant ek dafa try karein (kaam aata hai agar page already logged-in state mein reload hui ho)
  refreshAuthToken();

  if (refreshIntervalId) clearInterval(refreshIntervalId);
  refreshIntervalId = setInterval(
    () => {
      refreshAuthToken();
    },
    60 * 1000, // har 1 minute check karein — asal refresh sirf zaroorat par hoga (exp check ke zariye)
  );
};

export const stopTokenRefresh = () => {
  if (refreshIntervalId) {
    clearInterval(refreshIntervalId);
    refreshIntervalId = null;
  }
};
